import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Search, Send, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import { EmptyState, Spinner, fmtDay, gbp2, inputCls } from "@/components/portal/shared";

const INV_STYLES = {
  Paid: "bg-emerald-100 text-emerald-900",
  "Part Paid": "bg-amber-100 text-amber-900",
  Issued: "bg-slate-100 text-slate-600",
};

export default function InvoicesPage() {
  const { api } = usePortalAuth();
  const [invoices, setInvoices] = useState(null);
  const [q, setQ] = useState("");
  const [emailBusy, setEmailBusy] = useState("");

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data.invoices)).catch(() => setInvoices([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const emailInvoice = async (inv) => {
    if (emailBusy) return;
    setEmailBusy(inv.id);
    try {
      const r = await api.post(`/invoices/${inv.id}/email`);
      toast.success(`Invoice ${inv.ref} emailed to ${r.data.emailed_to}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEmailBusy("");
    }
  };

  if (invoices === null) return <Spinner />;

  const rows = invoices.filter((i) =>
    `${i.ref} ${i.order_ref} ${i.customer}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="invoices-page" className="space-y-5">
      <div className="relative max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input data-testid="invoices-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice, order or customer…" className={`${inputCls} pl-10 rounded-full`} />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Receipt}>No invoices yet — an invoice is generated automatically for every order.</EmptyState>
      ) : (
        <div className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[780px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-warm-200 bg-warm-100/60">
                <th className="px-5 py-3">Invoice</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Paid</th>
                <th className="px-4 py-3">Outstanding</th>
                <th className="px-4 py-3">Issued</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-200">
              {rows.map((i) => (
                <tr key={i.id} className="hover:bg-warm-100/50 transition-colors" data-testid={`invoice-row-${i.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-navy-900">{i.ref}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-500">{i.order_ref}</td>
                  <td className="px-4 py-3.5">
                    <Link to={`/portal/customers/${i.lead_id}?tab=payments`} className="font-medium text-slate-800 hover:underline">{i.customer}</Link>
                  </td>
                  <td className="px-4 py-3.5 font-semibold">{gbp2(i.total)}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-600">{gbp2((i.deposit_paid || 0) + (i.balance_paid || 0))}</td>
                  <td className={`px-4 py-3.5 font-semibold ${i.outstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>{gbp2(i.outstanding)}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-400">{fmtDay(i.created_at)}</td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span data-testid={`invoice-status-${i.id}`} className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold ${INV_STYLES[i.status] || INV_STYLES.Issued}`}>{i.status}</span>
                      <button
                        type="button"
                        data-testid={`invoice-email-${i.id}`}
                        disabled={emailBusy === i.id}
                        onClick={() => emailInvoice(i)}
                        title={i.emailed_at ? `Emailed ${new Date(i.emailed_at).toLocaleDateString("en-GB")} — send again` : "Email invoice to customer"}
                        className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40 disabled:opacity-40"
                      >
                        {emailBusy === i.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                        {i.emailed_at ? "Resend" : "Email"}
                      </button>
                      <Link
                        to={`/portal/invoices/${i.id}/print`}
                        data-testid={`invoice-print-${i.id}`}
                        title="Print-friendly invoice"
                        className="inline-flex items-center gap-1 rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-navy-800/40"
                      >
                        <Printer className="h-3 w-3" /> PDF
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
